import { useState } from "react";

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;

/**
 * The Z S MERCH shopping app (shop-app/, package za.co.zsmerch.shop). Live on Google Play since
 * Oct 2026, on the App Store (ZA) since 28 Oct 2026 (app 6822748850). The App Store badge is
 * Apple's official one, unmodified (developer.apple.com/assets/elements/badges/). iPhone Safari also
 * gets Apple's own Smart App Banner from the `apple-itunes-app` meta tag (shop-seo.ts `headTags`),
 * which is why the in-page install bar below stays Android-only.
 */
export const APP_LINKS: { android: string | null; ios: string | null } = {
  android: "https://play.google.com/store/apps/details?id=za.co.zsmerch.shop",
  ios: "https://apps.apple.com/za/app/z-s-merch/id6822748850",
};

/**
 * What the app does TODAY (screens in shop-app/src/screens). It has no push notifications or
 * scanner yet — never promise those here until they ship.
 */
const PERKS: ReadonlyArray<readonly [string, string]> = [
  ["Deal of the Day", "Today's deal and clearance, a tap away."],
  ["Wishlist", "Save what you like and come back to it."],
  ["Your orders", "See every order and track your delivery."],
  ["Wholesale prices", "Sign in with your wholesale account to see wholesale prices."],
];

/** Google's official badge, unmodified (their brand rules). The PNG carries its own padding. */
export function StoreBadges({ where }: { where: string }) {
  return (
    <div className="app-badges">
      {APP_LINKS.android && (
        <a href={APP_LINKS.android} {...EXT} className="app-badge" data-where={where}>
          <img src="/brand/apps/google-play-badge.png" alt="Get it on Google Play" width={168} height={65} loading="lazy" decoding="async" />
        </a>
      )}
      {APP_LINKS.ios && (
        <a href={APP_LINKS.ios} {...EXT} className="app-badge" data-where={where}>
          <img src="/brand/apps/app-store-badge.svg" alt="Download on the App Store" width={150} height={50} loading="lazy" decoding="async" />
        </a>
      )}
    </div>
  );
}

/** The home-page section. Renders nothing while no store link exists. */
export function HomeAppSection() {
  if (!APP_LINKS.android && !APP_LINKS.ios) return null;
  return (
    <section className="wrap home-sec" aria-labelledby="h-app">
      <div className="app-promo">
        <div>
          <h2 id="h-app">Get the Z S MERCH app</h2>
          <p className="app-promo-lead">Shop the whole range from your phone. Free{APP_LINKS.ios ? "" : " on Android"}.</p>
          <ul className="app-perks">
            {PERKS.map(([head, body]) => <li key={head}><b>{head}</b>{body}</li>)}
          </ul>
          <StoreBadges where="home" />
        </div>
        <img className="app-promo-mark" src="/brand/zs-mark.png" alt="" width={86} height={132} loading="lazy" decoding="async" />
      </div>
    </section>
  );
}

const DISMISS_KEY = "zs-app-banner-dismissed";
/** A dismissed banner stays away this long, then may ask once more. */
const DISMISS_DAYS = 30;

/**
 * A slim bar on ANDROID phones only, above the header. Desktop and iPhone never see it (no iOS app
 * yet). Closing it is remembered per browser; storage can be blocked, so every access is guarded.
 */
export function AppBanner() {
  /**
   * Decided in the FIRST render, not an effect: the shop is client-rendered (createRoot), so the
   * bar is there from the first paint instead of pushing the page down a moment later.
   */
  const [show, setShow] = useState(() => {
    if (!APP_LINKS.android || !/Android/i.test(navigator.userAgent)) return false;
    let dismissedAt = 0;
    try {
      dismissedAt = Number(localStorage.getItem(DISMISS_KEY)) || 0;
    } catch {
      /* storage blocked: show it, just can't remember a close */
    }
    return Date.now() - dismissedAt > DISMISS_DAYS * 86_400_000;
  });
  if (!show || !APP_LINKS.android) return null;
  const close = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* nothing to remember with */
    }
  };
  return (
    <div className="app-banner" role="region" aria-label="Get our app">
      <img src="/brand/zs-mark-40.webp" srcSet="/brand/zs-mark-40.webp 1x, /brand/zs-mark-40@2x.webp 2x" alt="" width={21} height={32} />
      <span className="app-banner-text"><b>Z S MERCH app</b>Free on Google Play</span>
      {/* Install does NOT dismiss: if the install is abandoned, the bar is still there next visit. */}
      <a href={APP_LINKS.android} {...EXT} className="btn app-banner-go">Install</a>
      <button type="button" className="app-banner-x" onClick={close} aria-label="Close">×</button>
    </div>
  );
}
